const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const hoverQuery = window.matchMedia('(any-hover: hover) and (any-pointer: fine)');
const atelierLunarPhaseNames = [
  'New moon',
  'Waxing crescent moon',
  'First quarter moon',
  'Waxing gibbous moon',
  'Full moon',
  'Waning gibbous moon',
  'Last quarter moon',
  'Waning crescent moon',
];

const atelierLunarPhaseIndex = () => {
  const synodicMonth = 29.530588853;
  const knownNewMoon = Date.UTC(2000, 0, 6, 18, 14);
  const daysSinceNewMoon = (Date.now() - knownNewMoon) / 86400000;
  const lunarAge = ((daysSinceNewMoon % synodicMonth) + synodicMonth) % synodicMonth;
  return Math.round((lunarAge / synodicMonth) * 8) % 8;
};

const updateAtelierRegister = (root) => {
  const phaseName = atelierLunarPhaseNames[atelierLunarPhaseIndex()];
  root.querySelectorAll('[data-atelier-lunar-label]').forEach((element) => {
    element.textContent = phaseName;
  });

  const now = new Date();
  root.querySelectorAll('[data-atelier-current-date]').forEach((element) => {
    element.textContent = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(now);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    element.setAttribute('datetime', `${year}-${month}-${day}`);
  });
};

const allowsLivingMotion = () =>
  document.body.dataset.atelierMotion !== 'none' &&
  document.body.classList.contains('atelier-living-details') &&
  !motionQuery.matches &&
  !window.Shopify?.designMode;

const atelierScrollState = () => {
  const container = document.querySelector('.page-wrapper');
  if (container instanceof HTMLElement && container.scrollHeight > container.clientHeight) {
    const range = Math.max(1, container.scrollHeight - container.clientHeight);
    return { container, progress: container.scrollTop / range };
  }

  const range = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
  return { container: window, progress: window.scrollY / range };
};

class AtelierMoonCycle extends HTMLElement {
  #button;
  #basePhase = 1;
  #cycleTimer;
  #phase = 1;
  #scrollStep = 0;
  #atmosphereHandler;
  #clickHandler;
  #pointerEnterHandler;
  #pointerLeaveHandler;
  #visibilityHandler;

  connectedCallback() {
    this.#button = this.querySelector('button');
    if (!this.#button) return;

    this.#basePhase = this.#initialPhase();
    this.#phase = this.#basePhase;
    this.#atmosphereHandler = (event) => this.#applyScrollProgress(event.detail?.progress || 0);
    this.#clickHandler = () => this.#advance();
    this.#pointerEnterHandler = () => this.#startCycling();
    this.#pointerLeaveHandler = () => this.#stopCycling();
    this.#visibilityHandler = () => {
      if (document.hidden) this.#stopCycling();
    };

    this.#button.addEventListener('click', this.#clickHandler);
    this.#button.addEventListener('pointerenter', this.#pointerEnterHandler);
    this.#button.addEventListener('pointerleave', this.#pointerLeaveHandler);
    document.addEventListener('visibilitychange', this.#visibilityHandler);
    window.addEventListener('atelier:atmosphere', this.#atmosphereHandler);
    this.#applyScrollProgress(this.#currentScrollProgress());
    this.#render();
  }

  disconnectedCallback() {
    this.#stopCycling();
    this.#button?.removeEventListener('click', this.#clickHandler);
    this.#button?.removeEventListener('pointerenter', this.#pointerEnterHandler);
    this.#button?.removeEventListener('pointerleave', this.#pointerLeaveHandler);
    document.removeEventListener('visibilitychange', this.#visibilityHandler);
    window.removeEventListener('atelier:atmosphere', this.#atmosphereHandler);
  }

  #advance() {
    this.#basePhase = (this.#basePhase + 1) % 8;
    this.#phase = (this.#basePhase + this.#scrollStep) % 8;
    this.#render();
  }

  #applyScrollProgress(progress) {
    if (document.body.dataset.atelierScrollAtmosphere !== 'true') return;

    const boundedProgress = Math.min(1, Math.max(0, progress));
    const nextStep = Math.min(4, Math.floor(boundedProgress * 4.001));
    if (nextStep === this.#scrollStep) return;

    this.#scrollStep = nextStep;
    this.#phase = (this.#basePhase + this.#scrollStep) % 8;
    this.#render();
  }

  #currentScrollProgress() {
    return atelierScrollState().progress;
  }

  #initialPhase() {
    if (document.body.dataset.atelierRealMoon !== 'true') {
      this.dataset.lunarSource = 'illustrative';
      return Number.parseInt(this.dataset.phase || '1', 10);
    }

    this.dataset.lunarSource = 'current';
    return atelierLunarPhaseIndex();
  }

  #startCycling() {
    if (!hoverQuery.matches || !allowsLivingMotion() || this.#cycleTimer) return;
    this.#cycleTimer = window.setInterval(() => this.#advance(), 560);
  }

  #stopCycling() {
    if (!this.#cycleTimer) return;
    window.clearInterval(this.#cycleTimer);
    this.#cycleTimer = undefined;
  }

  #render() {
    this.dataset.phase = String(this.#phase);
    this.#button?.setAttribute('aria-label', `${atelierLunarPhaseNames[this.#phase]}. Change moon phase`);
  }
}

class AtelierAtmosphere extends HTMLElement {
  #frame;
  #motionChangeHandler;
  #viewportHandler;

  connectedCallback() {
    this.#viewportHandler = () => this.#requestUpdate();
    this.#motionChangeHandler = () => this.#configure();
    motionQuery.addEventListener('change', this.#motionChangeHandler);
    this.#configure();
  }

  disconnectedCallback() {
    motionQuery.removeEventListener('change', this.#motionChangeHandler);
    this.#disconnect();
  }

  #configure() {
    this.#disconnect();

    if (!allowsLivingMotion() || document.body.dataset.atelierScrollAtmosphere !== 'true') {
      this.style.setProperty('--atelier-scroll-glow', '0');
      this.style.setProperty('--atelier-scroll-stars', '0');
      this.style.setProperty('--atelier-scroll-hue', '0deg');
      return;
    }

    document.addEventListener('scroll', this.#viewportHandler, { passive: true, capture: true });
    window.addEventListener('scroll', this.#viewportHandler, { passive: true });
    window.addEventListener('resize', this.#viewportHandler);
    this.#update();
  }

  #requestUpdate() {
    if (this.#frame) return;
    this.#frame = window.requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  }

  #update() {
    const progress = Math.min(1, Math.max(0, atelierScrollState().progress));
    const stars = Math.max(0, (progress - 0.16) / 0.84);

    this.style.setProperty('--atelier-scroll-glow', (progress * 0.16).toFixed(4));
    this.style.setProperty('--atelier-scroll-stars', (stars * 0.18).toFixed(4));
    this.style.setProperty('--atelier-scroll-hue', `${(progress * 24 - 5).toFixed(2)}deg`);
    this.dataset.depth = String(Math.min(4, Math.floor(progress * 4.001)));
    window.dispatchEvent(new CustomEvent('atelier:atmosphere', { detail: { progress } }));
  }

  #disconnect() {
    document.removeEventListener('scroll', this.#viewportHandler, { capture: true });
    window.removeEventListener('scroll', this.#viewportHandler);
    window.removeEventListener('resize', this.#viewportHandler);
    if (this.#frame) window.cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }
}

class AtelierCandlelight extends HTMLElement {
  #hideTimer;
  #pointerDownHandler;

  connectedCallback() {
    this.#pointerDownHandler = (event) => this.#onPointerDown(event);

    window.addEventListener('pointerdown', this.#pointerDownHandler, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('pointerdown', this.#pointerDownHandler);
    if (this.#hideTimer) window.clearTimeout(this.#hideTimer);
  }

  #onPointerDown(event) {
    if (!allowsLivingMotion() || (event.pointerType === 'mouse' && event.button !== 0)) return;

    this.style.setProperty('--atelier-candle-x', `${event.clientX}px`);
    this.style.setProperty('--atelier-candle-y', `${event.clientY}px`);
    this.setAttribute('data-active', '');

    if (this.#hideTimer) window.clearTimeout(this.#hideTimer);
    this.#hideTimer = window.setTimeout(() => {
      this.removeAttribute('data-active');
      this.#hideTimer = undefined;
    }, 240);
  }
}

class AtelierPageRitual extends HTMLElement {
  #clickHandler;
  #navigationTimer;
  #pageShowHandler;

  connectedCallback() {
    this.#clickHandler = (event) => this.#onClick(event);
    this.#pageShowHandler = () => this.#reset();
    document.addEventListener('click', this.#clickHandler);
    window.addEventListener('pageshow', this.#pageShowHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#clickHandler);
    window.removeEventListener('pageshow', this.#pageShowHandler);
    if (this.#navigationTimer) window.clearTimeout(this.#navigationTimer);
  }

  #onClick(event) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !allowsLivingMotion() ||
      document.body.dataset.atelierPageRitual !== 'true'
    ) {
      return;
    }

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (
      !link ||
      link.hasAttribute('download') ||
      link.target === '_blank' ||
      link.matches('[aria-disabled="true"], [data-skip-page-transition]')
    ) {
      return;
    }

    const destination = new URL(link.href, window.location.href);
    const current = new URL(window.location.href);
    const isSamePageHash =
      destination.origin === current.origin &&
      destination.pathname === current.pathname &&
      destination.search === current.search &&
      destination.hash;

    if (
      destination.origin !== current.origin ||
      !['http:', 'https:'].includes(destination.protocol) ||
      destination.href === current.href ||
      isSamePageHash
    ) {
      return;
    }

    event.preventDefault();
    document.documentElement.classList.add('atelier-page-leaving');
    this.setAttribute('data-active', '');
    this.#navigationTimer = window.setTimeout(() => window.location.assign(destination.href), 460);
  }

  #reset() {
    document.documentElement.classList.remove('atelier-page-leaving');
    this.removeAttribute('data-active');
    if (this.#navigationTimer) window.clearTimeout(this.#navigationTimer);
    this.#navigationTimer = undefined;
  }
}

class AtelierChapterIndex extends HTMLElement {
  #closeButtons;
  #closeHandler;
  #closeTimer;
  #dialog;
  #dialogCancelHandler;
  #openHandler;
  #trigger;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#trigger = this.querySelector('[data-atelier-index-open]');
    this.#closeButtons = this.querySelectorAll('[data-atelier-index-close]');
    if (!this.#dialog || !this.#trigger) return;

    this.#openHandler = () => this.#open();
    this.#closeHandler = () => this.#close();
    this.#dialogCancelHandler = (event) => {
      event.preventDefault();
      this.#close();
    };

    this.#trigger.addEventListener('click', this.#openHandler);
    this.#closeButtons.forEach((button) => button.addEventListener('click', this.#closeHandler));
    this.#dialog.addEventListener('cancel', this.#dialogCancelHandler);
    updateAtelierRegister(this);
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.#openHandler);
    this.#closeButtons?.forEach((button) => button.removeEventListener('click', this.#closeHandler));
    this.#dialog?.removeEventListener('cancel', this.#dialogCancelHandler);
    if (this.#closeTimer) window.clearTimeout(this.#closeTimer);
    document.body.classList.remove('atelier-index-open');
  }

  #open() {
    if (!this.#dialog || !this.#trigger || this.#dialog.open) return;
    if (this.#closeTimer) window.clearTimeout(this.#closeTimer);

    this.#dialog.showModal();
    this.#trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('atelier-index-open');
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.setAttribute('data-open', '');
        this.#dialog?.querySelector('#AtelierChapterIndexTitle')?.focus();
      });
    });
  }

  #close() {
    if (!this.#dialog?.open || !this.#trigger) return;

    this.removeAttribute('data-open');
    this.#trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('atelier-index-open');
    this.#closeTimer = window.setTimeout(() => {
      this.#dialog?.close();
      this.#trigger?.focus();
      this.#closeTimer = undefined;
    }, 420);
  }
}

class AtelierColophon extends HTMLElement {
  connectedCallback() {
    updateAtelierRegister(this);
  }
}

class AtelierBlinkingEye extends HTMLElement {
  #button;
  #blinkTimer;
  #idleTimer;
  #clickHandler;
  #pointerEnterHandler;
  #pointerLeaveHandler;
  #visibilityHandler;

  connectedCallback() {
    this.#button = this.querySelector('button');
    if (!this.#button) return;

    this.#clickHandler = () => this.#blink(520);
    this.#pointerEnterHandler = () => this.classList.add('is-closed');
    this.#pointerLeaveHandler = () => this.classList.remove('is-closed');
    this.#visibilityHandler = () => {
      if (document.hidden) this.#clearTimers();
      else this.#scheduleIdleBlink();
    };

    this.#button.addEventListener('click', this.#clickHandler);
    this.#button.addEventListener('pointerenter', this.#pointerEnterHandler);
    this.#button.addEventListener('pointerleave', this.#pointerLeaveHandler);
    document.addEventListener('visibilitychange', this.#visibilityHandler);
    this.#scheduleIdleBlink();
  }

  disconnectedCallback() {
    this.#clearTimers();
    this.#button?.removeEventListener('click', this.#clickHandler);
    this.#button?.removeEventListener('pointerenter', this.#pointerEnterHandler);
    this.#button?.removeEventListener('pointerleave', this.#pointerLeaveHandler);
    document.removeEventListener('visibilitychange', this.#visibilityHandler);
  }

  #blink(duration = 180) {
    if (this.#blinkTimer) window.clearTimeout(this.#blinkTimer);
    this.classList.add('is-blinking');
    this.#blinkTimer = window.setTimeout(() => {
      this.classList.remove('is-blinking');
      this.#blinkTimer = undefined;
    }, duration);
  }

  #scheduleIdleBlink() {
    if (!allowsLivingMotion() || document.hidden || this.#idleTimer) return;
    const delay = 6200 + Math.random() * 5200;
    this.#idleTimer = window.setTimeout(() => {
      this.#idleTimer = undefined;
      this.#blink();
      this.#scheduleIdleBlink();
    }, delay);
  }

  #clearTimers() {
    if (this.#blinkTimer) window.clearTimeout(this.#blinkTimer);
    if (this.#idleTimer) window.clearTimeout(this.#idleTimer);
    this.#blinkTimer = undefined;
    this.#idleTimer = undefined;
    this.classList.remove('is-blinking', 'is-closed');
  }
}

class AtelierMotionRoot extends HTMLElement {
  #intersectionObserver;
  #mutationObserver;
  #motionChangeHandler;
  #scrollFrame;
  #viewportChangeHandler;

  connectedCallback() {
    this.#motionChangeHandler = () => this.#configure();
    motionQuery.addEventListener('change', this.#motionChangeHandler);
    this.#configure();
  }

  disconnectedCallback() {
    motionQuery.removeEventListener('change', this.#motionChangeHandler);
    this.#disconnectObservers();
  }

  #configure() {
    this.#disconnectObservers();

    const motionLevel = document.body.dataset.atelierMotion;
    const shouldAnimate =
      motionLevel !== 'none' &&
      !motionQuery.matches &&
      !window.Shopify?.designMode &&
      'IntersectionObserver' in window;

    this.#registerMarginalia(this);

    if (!shouldAnimate) {
      this.removeAttribute('data-motion-ready');
      this.querySelectorAll('[data-atelier-reveal]').forEach((element) => {
        element.classList.remove('atelier-reveal-pending');
        element.classList.add('atelier-reveal-visible');
      });
      this.querySelectorAll('[data-atelier-divider]').forEach((element) => {
        element.classList.remove('atelier-divider-pending');
        element.classList.add('atelier-divider-visible');
      });
      return;
    }

    this.setAttribute('data-motion-ready', '');
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const element = entry.target;

          if (element.hasAttribute('data-atelier-divider')) {
            element.classList.remove('atelier-divider-pending');
            element.classList.add('atelier-divider-visible');
            this.#intersectionObserver?.unobserve(element);
            continue;
          }

          element.classList.remove('atelier-reveal-pending');
          element.classList.add('atelier-reveal-visible');
          this.#intersectionObserver?.unobserve(element);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    this.#viewportChangeHandler = () => {
      if (this.#scrollFrame) return;
      this.#scrollFrame = window.requestAnimationFrame(() => {
        this.#scrollFrame = undefined;
        this.#revealVisible();
      });
    };
    window.addEventListener('scroll', this.#viewportChangeHandler, { passive: true });
    window.addEventListener('resize', this.#viewportChangeHandler);

    this.#register(this);
    this.#mutationObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element) this.#register(node);
        }
      }
    });
    this.#mutationObserver.observe(this, { childList: true, subtree: true });
  }

  #register(root) {
    this.#registerMarginalia(root);
    this.#registerDividers(root);

    const elements = [];
    if (root instanceof Element && root.matches('[data-atelier-reveal]')) elements.push(root);
    elements.push(...root.querySelectorAll('[data-atelier-reveal]'));

    for (const element of elements) {
      if (element.classList.contains('atelier-reveal-visible')) continue;

      const bounds = element.getBoundingClientRect();
      const isInitiallyVisible = bounds.top < window.innerHeight * 0.94 && bounds.bottom > 0;

      if (isInitiallyVisible) {
        element.classList.add('atelier-reveal-visible');
        continue;
      }

      element.classList.add('atelier-reveal-pending');
      this.#intersectionObserver?.observe(element);
    }
  }

  #registerMarginalia(root) {
    if (
      !document.body.classList.contains('template-index') ||
      document.body.dataset.atelierMarginalia !== 'true'
    ) {
      return;
    }

    const notes = [
      ['MARGINALIA · 01', 'A thought kept between chapters.'],
      ['FOLIO · I', 'The story presently unfolding.'],
      ['FIELD NOTE · 02', 'Colour gathered through a darker lens.'],
      ['CATALOGUE · 03', 'Objects selected for this chapter.'],
      ['MATERIAL STUDY · 04', 'The contents are part of the telling.'],
      ['CURATED SERIES · 05', 'Compositions made to be opened slowly.'],
      ['REGISTER · VI', 'Earlier chapters remain on the shelf.'],
      ['LETTER · VII', 'Sent only when there is something to say.'],
    ];
    const sections = [];

    if (root === this) {
      sections.push(...this.children);
    } else if (root instanceof Element && root.parentElement === this) {
      sections.push(root);
    }

    for (const section of sections) {
      if (!section.classList.contains('shopify-section') || section === this.firstElementChild) continue;
      if (section.querySelector(':scope > .atelier-marginalia')) continue;

      const sectionIndex = [...this.children].indexOf(section) - 1;
      const note = notes[sectionIndex] || [
        `FOLIO · ${String(sectionIndex + 1).padStart(2, '0')}`,
        'A further observation from the atelier.',
      ];
      const marginalia = document.createElement('aside');
      const folio = document.createElement('span');
      const copy = document.createElement('span');

      marginalia.className = 'atelier-marginalia';
      marginalia.setAttribute('aria-hidden', 'true');
      marginalia.setAttribute('data-atelier-reveal', '');
      folio.className = 'atelier-marginalia__folio';
      copy.className = 'atelier-marginalia__copy';
      folio.textContent = note[0];
      copy.textContent = note[1];
      marginalia.append(folio, copy);
      section.append(marginalia);
    }
  }

  #registerDividers(root) {
    if (
      !document.body.classList.contains('template-index') ||
      document.body.dataset.atelierDividers !== 'true'
    ) {
      return;
    }

    const sections = [];
    if (root === this) {
      sections.push(...this.children);
    } else if (root instanceof Element && root.parentElement === this) {
      sections.push(root);
    }

    for (const section of sections) {
      if (!section.classList.contains('shopify-section') || section === this.firstElementChild) continue;
      if (section.hasAttribute('data-atelier-divider')) continue;

      section.setAttribute('data-atelier-divider', '');
      section.classList.add('atelier-divider-pending');
      this.#intersectionObserver?.observe(section);
    }
  }

  #revealVisible() {
    this.querySelectorAll('.atelier-reveal-pending').forEach((element) => {
      const bounds = element.getBoundingClientRect();
      const isVisible = bounds.top < window.innerHeight * 0.94 && bounds.bottom > 0;
      if (!isVisible) return;

      element.classList.remove('atelier-reveal-pending');
      element.classList.add('atelier-reveal-visible');
      this.#intersectionObserver?.unobserve(element);
    });
  }

  #disconnectObservers() {
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    if (this.#viewportChangeHandler) {
      window.removeEventListener('scroll', this.#viewportChangeHandler);
      window.removeEventListener('resize', this.#viewportChangeHandler);
    }
    if (this.#scrollFrame) window.cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = undefined;
  }
}

if (!customElements.get('atelier-motion-root')) {
  customElements.define('atelier-motion-root', AtelierMotionRoot);
}

if (!customElements.get('atelier-moon-cycle')) {
  customElements.define('atelier-moon-cycle', AtelierMoonCycle);
}

if (!customElements.get('atelier-blinking-eye')) {
  customElements.define('atelier-blinking-eye', AtelierBlinkingEye);
}

if (!customElements.get('atelier-atmosphere')) {
  customElements.define('atelier-atmosphere', AtelierAtmosphere);
}

if (!customElements.get('atelier-candlelight')) {
  customElements.define('atelier-candlelight', AtelierCandlelight);
}

if (!customElements.get('atelier-page-ritual')) {
  customElements.define('atelier-page-ritual', AtelierPageRitual);
}

if (!customElements.get('atelier-chapter-index')) {
  customElements.define('atelier-chapter-index', AtelierChapterIndex);
}

if (!customElements.get('atelier-colophon')) {
  customElements.define('atelier-colophon', AtelierColophon);
}
